import { useRef, type ReactNode } from "react";
import { TemplateFileStatus } from "src/pages/projects/library/files/template-file-status";
import { formatDistanceToNowStrict } from "date-fns";
import {
  Box,
  Column,
  FavoritedIcon,
  FavoriteIcon,
  FileIcon,
  HtMedia,
  IconButton,
  Row,
  SkeletonBox,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";
import type { VideoComposition } from "@hightouch/ad-studio-renderer";
import type { AmpLibraryFileFragment } from "src/graphql";
import { AdAssetPreview } from "src/pages/ad-studio/components/ad-asset-preview";
import { THUMBNAIL_RESIZE_WIDTH } from "src/pages/ad-studio/components/ad-grid-metrics";
import { CardSelectionCheckbox } from "src/pages/ad-studio/components/card-selection-checkbox";
import { WebPagePreview } from "src/pages/ad-studio/components/web-page-preview";
import { CanvasThumbnail } from "src/pages/projects/library/canvas/canvas-thumbnail";
import { FileMenu } from "src/pages/projects/library/file-menu";
import { getFileType } from "src/pages/projects/library/files/file-type";
import { isImageUpload } from "@hightouch/lib/llm/files/types";
import { useFileDownload } from "src/pages/projects/library/files/use-file-download";
import { useOptimisticFavorite } from "src/pages/projects/library/files/use-optimistic-favorite";
import { DocMiniature } from "src/pages/projects/library/files/doc-miniature";
import { MessageFilePreview } from "src/pages/projects/library/files/message-file-preview";
import { PHONE_FRAME_WIDTH } from "src/pages/lifecycle-studio/copy/components/preview/phone-frame";
import type { FileSelection } from "src/pages/projects/library/use-file-selection";
import {
  DraftBadge,
  PrivateBadge,
} from "src/pages/projects/library/files/file-badges";

const PAPER_WIDTH = 44;
/** The paper is 176px wide (sizes 44) and the thumbnail 224px tall (sizes 56); emails and docs scale down to fit it. */
const PAPER_WIDTH_PX = 176;
const EMAIL_WIDTH = 600;
const EMAIL_SCALE = PAPER_WIDTH_PX / EMAIL_WIDTH;
const EMAIL_HEIGHT = Math.ceil(224 / EMAIL_SCALE);
/** The width SMS and push previews are laid out at before they scale down to the paper. */
const MESSAGE_WIDTH = { sms: PHONE_FRAME_WIDTH, push: 320 };
/** The 224px thumbnail plus the name and edit time beneath it. */
export const FILE_CARD_HEIGHT = 300;

export type FileCardProps = {
  file: AmpLibraryFileFragment;
  onOpen: (file: AmpLibraryFileFragment) => void;
  /** Called once the file's own menu has deleted it. */
  onDeleted: (id: string) => void;
  /** Absent where the files take no bulk actions, which leaves the card without its selector. */
  selection?: FileSelection;
  /** Every file shown is a draft, so none needs a Draft badge. */
  isDraftsView?: boolean;
};

/** A file as a card: its object on a quiet plane, then its name and when it last changed. The whole card opens it. */
export function FileCard({
  file,
  onOpen,
  onDeleted,
  selection,
  isDraftsView = false,
}: FileCardProps) {
  const download = useFileDownload();
  const favorite = useOptimisticFavorite(file);
  const name = file.name || "Untitled";
  const type = getFileType(file);
  const isSelected = selection?.isSelected(file.id) ?? false;
  const isSelecting = (selection?.selected.length ?? 0) > 0;
  const isRangeClick = useRef(false);
  return (
    <Box
      data-group
      position="relative"
      height="100%"
      isolation="isolate"
      bg="white"
      borderRadius="2xl"
      boxShadow="sm"
      _hover={{ boxShadow: "md" }}
      outline={isSelected ? "2px solid" : undefined}
      outlineColor="primary.base"
      // A focused card shows its hidden controls, as hover does; :has has no pseudo prop.
      // An open menu or move picker keeps them showing too; both mark their button aria-expanded.
      sx={{
        "&:has(:focus-visible) [data-card-control]": { opacity: 1 },
        "&:has(:focus-visible) [data-card-select]": {
          opacity: 1,
          visibility: "visible",
        },
        "&:has([data-card-control] [aria-expanded=true]) [data-card-control]": {
          opacity: 1,
        },
      }}
    >
      <Box
        height={56}
        overflow="hidden"
        borderTopRadius="2xl"
        bg="base.lightBackground"
        borderBottomWidth="hairline"
        borderColor="base.divider"
        pointerEvents="none"
        aria-hidden
      >
        <FileThumbnail file={file} />
      </Box>
      <Column px={4} pt={3} pb={3.5} gap={1.5} minWidth={0}>
        <Row gap={2} align="center" minWidth={0}>
          <Box
            as={type.icon}
            boxSize={4}
            flexShrink={0}
            color="text.secondary"
          />
          <Box
            as="button"
            type="button"
            flex={1}
            minWidth={0}
            textAlign="left"
            aria-label={`Open ${name}, ${type.label.toLowerCase()}`}
            onClick={() => onOpen(file)}
            _after={{
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "2xl",
            }}
            _focusVisible={{ _after: { boxShadow: "outline" } }}
          >
            <Box position="relative" zIndex={1}>
              <TextWithTooltip fontWeight="medium">{name}</TextWithTooltip>
            </Box>
          </Box>
        </Row>
        <Row gap={2} align="center" minHeight={5} minWidth={0}>
          {file.adTemplate ? (
            <TemplateFileStatus status={file.adTemplate.status} />
          ) : (
            file.isDraft && !isDraftsView && <DraftBadge size="sm" />
          )}
          {file.visibility === "private" && <PrivateBadge size="sm" />}
          <TextWithTooltip size="sm" color="text.secondary">
            Edited{" "}
            {formatDistanceToNowStrict(new Date(file.updatedAt), {
              addSuffix: true,
            })}
          </TextWithTooltip>
        </Row>
      </Column>
      {/* Ad Studio's card selector: kept on every card once a selection starts, else on hover. */}
      {selection && (
        <Box
          data-card-select
          position="absolute"
          top={3}
          left={3}
          zIndex={2}
          // The white selector needs contrast over light thumbnails.
          bg="blackAlpha.40"
          borderRadius="full"
          opacity={isSelected || isSelecting ? 1 : 0}
          visibility={isSelected || isSelecting ? "visible" : "hidden"}
          _groupHover={{ opacity: 1, visibility: "visible" }}
          // The selector reports only the toggle, so the click that reaches it says whether Shift was held.
          onClickCapture={(event) => {
            isRangeClick.current = event.shiftKey;
          }}
        >
          <CardSelectionCheckbox
            isSelected={isSelected}
            label={`${isSelected ? "Deselect" : "Select"} ${name}`}
            onToggle={() => selection.toggle(file.id, isRangeClick.current)}
          />
        </Box>
      )}
      <Row
        position="absolute"
        top={3}
        right={3}
        zIndex={2}
        align="center"
        gap={1}
      >
        <Box data-card-control opacity={0} _groupHover={{ opacity: 1 }}>
          <FileMenu
            file={file}
            onOpen={onOpen}
            onDownload={download.requestDownload}
            onDeleted={onDeleted}
            variant="secondary"
          />
        </Box>
        {/* Ad Studio's card heart: always shown once favorited, else on hover. */}
        <Box
          data-card-control
          opacity={favorite.isFavorite ? 1 : 0}
          _groupHover={{ opacity: 1 }}
        >
          <IconButton
            aria-label={`${favorite.isFavorite ? "Unfavorite" : "Favorite"} ${name}`}
            icon={favorite.isFavorite ? FavoritedIcon : FavoriteIcon}
            size="sm"
            variant="secondary"
            onClick={() => void favorite.toggle()}
            // The secondary theme darkens the icon on hover and press; a favorite's heart stays red.
            {...(favorite.isFavorite && {
              sx: { svg: { color: "red.500" } },
              _hover: { svg: { color: "red.500" } },
              _active: { svg: { color: "red.500" } },
            })}
          />
        </Box>
      </Row>
      {download.dialog}
    </Box>
  );
}

export function FileCardSkeleton() {
  return (
    <Box
      height={`${FILE_CARD_HEIGHT}px`}
      borderRadius="2xl"
      boxShadow="sm"
      overflow="hidden"
    >
      <SkeletonBox height={56} borderRadius="none" />
      <Column px={4} pt={3} pb={3.5} gap={2.5}>
        <SkeletonBox height={4} width="60%" borderRadius="sm" />
        <SkeletonBox height={3} width="40%" borderRadius="sm" />
      </Column>
    </Box>
  );
}

/** A Library file's preview: its own content where it has some, else its type's icon. */
export function FileThumbnail({ file }: { file: AmpLibraryFileFragment }) {
  if (file.adTemplate?.previewUrl) {
    return (
      <HtMedia
        src={file.adTemplate.previewUrl}
        alt={file.name || "Template preview"}
        width="100%"
        height="100%"
        objectFit="contain"
      />
    );
  }
  if (file.slides?.slideUrls[0])
    return (
      <HtMedia
        src={file.slides.slideUrls[0]}
        alt={file.name || "Presentation"}
        width="100%"
        height="100%"
        objectFit="contain"
      />
    );
  if (
    file.generationStatus === "generating" ||
    file.generationStatus === "failed"
  )
    return (
      <Centered>
        <Text size="sm" color="text.secondary">
          {file.generationStatus === "failed"
            ? "Could not generate"
            : "Generating…"}
        </Text>
      </Centered>
    );
  if (file.canvas) return <CanvasThumbnail canvasId={file.canvas.canvasId} />;
  if (file.message) {
    const isSms = file.type === "sms";
    const width = MESSAGE_WIDTH[isSms ? "sms" : "push"];
    return (
      <Box position="relative" height="100%">
        <Box
          position="absolute"
          left="50%"
          top={isSms ? 4 : "50%"}
          width={`${width}px`}
          transformOrigin={isSms ? "top center" : "center"}
          transform={`translate(-50%, ${isSms ? "0" : "-50%"}) scale(${PAPER_WIDTH_PX / width})`}
          borderRadius="2xl"
          overflow="hidden"
        >
          <MessageFilePreview file={file} />
        </Box>
      </Box>
    );
  }
  if (file.html)
    return (
      <Centered>
        <Box width="100%" borderRadius="md" overflow="hidden" boxShadow="sm">
          <WebPagePreview
            content={file.html}
            versionId={file.versionId}
            variant="thumbnail"
          />
        </Box>
      </Centered>
    );
  if (file.email?.html)
    return (
      <Paper>
        <Box
          as="iframe"
          title={file.name || "Email"}
          srcDoc={file.email.html}
          sandbox=""
          tabIndex={-1}
          width={`${EMAIL_WIDTH}px`}
          height={`${EMAIL_HEIGHT}px`}
          border="none"
          transformOrigin="top left"
          transform={`scale(${EMAIL_SCALE})`}
        />
      </Paper>
    );
  if (file.type === "doc")
    return (
      <Paper>
        <DocMiniature file={file} width={PAPER_WIDTH_PX} />
      </Paper>
    );
  if (file.ad?.videoComposition) return <MotionAdThumbnail ad={file.ad} />;
  const image = file.ad
    ? file.ad.previewUrl ||
      (file.ad.svg
        ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(file.ad.svg)}`
        : null)
    : file.upload && isImageUpload(file.upload)
      ? file.upload.downloadUrl
      : null;
  if (image)
    return (
      <Centered>
        <HtMedia
          src={image}
          alt=""
          maxWidth="100%"
          maxHeight="100%"
          objectFit="contain"
          borderRadius="xs"
          boxShadow="sm"
          video={{ playback: "none" }}
        />
      </Centered>
    );
  return (
    <Centered>
      <Column align="center" gap={2} minWidth={0} maxWidth="100%">
        <Box as={FileIcon} boxSize={8} color="text.tertiary" />
        {file.upload?.filename && (
          <Box maxWidth="100%">
            <Text size="sm" color="text.secondary" isTruncated>
              {file.upload.filename}
            </Text>
          </Box>
        )}
      </Column>
    </Centered>
  );
}

/** A motion ad has no image of its own, so its composition draws its first frame, as the canvas does, contained in the thumbnail. */
function MotionAdThumbnail({
  ad,
}: {
  ad: NonNullable<AmpLibraryFileFragment["ad"]>;
}) {
  const composition = ad.videoComposition as VideoComposition;
  const ratio =
    composition.width > 0 && composition.height > 0
      ? composition.width / composition.height
      : 16 / 9;
  return (
    <Centered>
      <Row
        width="100%"
        height="100%"
        align="center"
        justify="center"
        sx={{ containerType: "size" }}
      >
        <Box
          width={`min(100cqw, 100cqh * ${ratio})`}
          overflow="hidden"
          borderRadius="xs"
          boxShadow="sm"
        >
          <AdAssetPreview
            asset={{
              video_composition: composition,
              // jsonb: the ad's storage keys, by filename.
              component_assets: ad.componentAssets as Record<
                string,
                string
              > | null,
            }}
            variant="thumbnail"
            playIconSize="2xl"
            thumbnailImageWidth={THUMBNAIL_RESIZE_WIDTH}
          />
        </Box>
      </Row>
    </Centered>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <Row height="100%" p={4} align="center" justify="center">
      {children}
    </Row>
  );
}

/** A page-shaped sheet rising from the plane, its foot cut off by the card. */
function Paper({ children }: { children: ReactNode }) {
  return (
    <Row height="100%" justify="center" pt={4}>
      <Box
        width={PAPER_WIDTH}
        height="100%"
        overflow="hidden"
        bg="white"
        borderTopRadius="md"
        boxShadow="sm"
      >
        {children}
      </Box>
    </Row>
  );
}
